import { createStoreSlice as createSlice, type PayloadAction } from '@/store/core';
import type { RootState } from '@/store';
import type { ISection, StringRecord } from '@/typings';
import type {
  BlockedEdit,
  FormField,
  IFormBase,
  IndexedAttestationForms,
  IndexedBlockedEdits,
  IndexedConditionalChildFields,
  IndexedConditionalFields,
  IndexedConditionalParentFields,
  IndexedErrors,
  IndexedMissingItemFormFields,
  IndexedSectionForms,
  IndexedStagedFieldsGroups,
  IndexedConditionalFieldsGroups,
} from '../typings';

interface FormState {
  loading: boolean;
  error: string | null;
  sectionForms: IndexedSectionForms;
  attestationForms: IndexedAttestationForms;
  missingItemFormFields: IndexedMissingItemFormFields;
  stagedFieldsGroups: IndexedStagedFieldsGroups;
  addedStagedFieldsGroup: StringRecord<number>;
  conditionalFieldsGroups: IndexedConditionalFieldsGroups;

  conditionalParentFields: IndexedConditionalParentFields;
  conditionalChildFields: IndexedConditionalChildFields;

  conditionalFields: IndexedConditionalFields;
  errorRegistry: IndexedErrors;
  blockedEdits: IndexedBlockedEdits;
}

type FormFieldGroupWithSectionId = {
  sectionId: ISection['id'];
  formFields: FormField[];
};

type StagedFieldsGroupWithFieldId = {
  fieldId: IFormBase['fieldId'];
  firstStageFieldsGroup: Array<IFormBase['fieldId']>;
};

const initialState: FormState = {
  sectionForms: {},
  attestationForms: {},
  missingItemFormFields: {},
  stagedFieldsGroups: {},
  loading: false,
  error: null,
  addedStagedFieldsGroup: {},
  conditionalParentFields: {},
  conditionalChildFields: {},
  conditionalFields: {},
  conditionalFieldsGroups: {},
  errorRegistry: {},
  blockedEdits: {},
};

export const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    formLoading: state => {
      state.loading = true;
    },
    fetchSectionFormSuccess: (
      state,
      action: PayloadAction<FormFieldGroupWithSectionId>,
    ) => {
      state.loading = false;
      state.error = null;
      state.sectionForms[action.payload.sectionId] = [
        ...action.payload.formFields,
      ];
    },
    fetchAttestationFormSuccess: (
      state,
      action: PayloadAction<IndexedAttestationForms>,
    ) => {
      state.loading = false;
      state.error = null;
      state.attestationForms = { ...state.attestationForms, ...action.payload };
    },
    setMissingItemFormFields: (
      state,
      action: PayloadAction<{ missingItemFormId: number; formFields: FormField[] }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.missingItemFormFields[action.payload.missingItemFormId] = [
        ...action.payload.formFields,
      ];
    },
    clearMissingItemFormFields: state => {
      state.missingItemFormFields = {};
    },
    setStagedFieldsGroup: (
      state,
      action: PayloadAction<StagedFieldsGroupWithFieldId>,
    ) => {
      state.stagedFieldsGroups[action.payload.fieldId] =
        action.payload.firstStageFieldsGroup;
    },
    removeStagedFieldsGroup: (state, action: PayloadAction<string>) => {
      delete state.stagedFieldsGroups[action.payload];
    },
    setAddedStagedFieldsGroup: (
      state,
      action: PayloadAction<{ group: string; index: number }>,
    ) => {
      state.addedStagedFieldsGroup[action.payload.group] = action.payload.index;
    },
    removeAddedStagedFieldsGroup: (state, action: PayloadAction<string>) => {
      delete state.addedStagedFieldsGroup[action.payload];
    },
    fetchFormFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setConditionalFields: (
      state,
      action: PayloadAction<IndexedConditionalFields>,
    ) => {
      state.conditionalFields = {
        ...state.conditionalFields,
        ...action.payload,
      };
    },
    setConditionalParentFields: (
      state,
      action: PayloadAction<IndexedConditionalParentFields>,
    ) => {
      state.conditionalParentFields = {
        ...state.conditionalParentFields,
        ...action.payload,
      };
    },
    setConditionalChildFields: (
      state,
      action: PayloadAction<IndexedConditionalChildFields>,
    ) => {
      state.conditionalChildFields = {
        ...state.conditionalChildFields,
        ...action.payload,
      };
    },
    setConditionalChildFieldHasAnyValue: (
      state,
      action: PayloadAction<{ fieldId: string; hasAnyValue: boolean }>,
    ) => {
      state.conditionalChildFields[action.payload.fieldId].hasAnyValue =
        action.payload.hasAnyValue;
    },
    setFormFieldError: (state, action: PayloadAction<IndexedErrors>) => {
      state.errorRegistry = {
        ...state.errorRegistry,
        ...action.payload,
      };
    },
    blockEdit: (
      state,
      action: PayloadAction<{ saveKey: string; edit: BlockedEdit }>,
    ) => {
      const { saveKey, edit } = action.payload;
      state.blockedEdits[saveKey] = {
        ...state.blockedEdits[saveKey],
        [edit.fieldId]: edit,
      };
    },
    clearBlockedEdit: (
      state,
      action: PayloadAction<{ saveKey: string; fieldId: string }>,
    ) => {
      const { saveKey, fieldId } = action.payload;
      const forKey = state.blockedEdits[saveKey];
      // Nothing is mutated when the field was never blocked, so Immer returns the
      // base state and dispatching unconditionally costs no re-render. Own-property
      // only: `in` walks the prototype, so a field id like `toString` would skip this.
      // Spelled the long way rather than Object.hasOwn: that is ES2022, above the
      // build's untranspiled baseline (no browserslist, so Vite targets safari14).
      if (!forKey || !Object.prototype.hasOwnProperty.call(forKey, fieldId)) {
        return;
      }

      delete forKey[fieldId];

      // Dropped rather than left empty so a form with no blocked edits leaves no key.
      if (Object.keys(forKey).length === 0) delete state.blockedEdits[saveKey];
    },
    clearBlockedEdits: (state, action: PayloadAction<{ saveKey: string }>) => {
      delete state.blockedEdits[action.payload.saveKey];
    },
    keepBlockedEditsFor: (
      state,
      action: PayloadAction<{ saveKey: string; fieldIds: string[] }>,
    ) => {
      const { saveKey, fieldIds } = action.payload;
      const forKey = state.blockedEdits[saveKey];
      if (!forKey) return;

      const kept = new Set(fieldIds);
      // Deleting only what goes keeps the base state when nothing does, as clearBlockedEdit.
      Object.keys(forKey).forEach(fieldId => {
        if (!kept.has(fieldId)) delete forKey[fieldId];
      });

      if (Object.keys(forKey).length === 0) delete state.blockedEdits[saveKey];
    },
    clearBlockedEditsFor: (
      state,
      action: PayloadAction<{ saveKey: string; fieldIds: string[] }>,
    ) => {
      const { saveKey, fieldIds } = action.payload;
      const forKey = state.blockedEdits[saveKey];
      if (!forKey) return;

      // Own-property only and nothing deleted when nothing is blocked, as clearBlockedEdit.
      fieldIds.forEach(fieldId => {
        if (Object.prototype.hasOwnProperty.call(forKey, fieldId)) {
          delete forKey[fieldId];
        }
      });

      if (Object.keys(forKey).length === 0) delete state.blockedEdits[saveKey];
    },
    setConditionalFieldsGroup: (
      state,
      action: PayloadAction<{
        groupId: IFormBase['fieldId'];
        conditionFieldId: IFormBase['fieldId'];
      }>,
    ) => {
      state.conditionalFieldsGroups[action.payload.groupId] =
        action.payload.conditionFieldId;
    },
    removeConditionalFieldsGroup: (
      state,
      action: PayloadAction<IFormBase['fieldId']>,
    ) => {
      delete state.conditionalFieldsGroups[action.payload];
    },
  },
});

export const {
  formLoading,
  fetchSectionFormSuccess,
  fetchAttestationFormSuccess,
  setMissingItemFormFields,
  clearMissingItemFormFields,
  setStagedFieldsGroup,
  removeStagedFieldsGroup,
  setAddedStagedFieldsGroup,
  removeAddedStagedFieldsGroup,
  fetchFormFailure,
  setConditionalFields,
  setConditionalParentFields,
  setConditionalChildFields,
  setConditionalChildFieldHasAnyValue,
  setFormFieldError,
  blockEdit,
  clearBlockedEdit,
  clearBlockedEdits,
  keepBlockedEditsFor,
  clearBlockedEditsFor,
  setConditionalFieldsGroup,
  removeConditionalFieldsGroup,
} = formSlice.actions;

export const selectForm = (state: RootState) =>
  state[formSlice.name] as typeof initialState;

// Module-level and frozen: useSyncExternalStore compares snapshots by reference,
// so a fresh {} here would make every store notification look like a change.
const NO_BLOCKED_EDITS: Readonly<Record<string, BlockedEdit>> = Object.freeze(
  {},
);

/** Blocked edits for one form, keyed by field id. Empty object when there are none. */
export const selectBlockedEdits =
  (saveKey: string | null) =>
  (state: RootState): Record<string, BlockedEdit> =>
    (saveKey ? selectForm(state).blockedEdits[saveKey] : undefined) ??
    (NO_BLOCKED_EDITS as Record<string, BlockedEdit>);
